"use client";

import { useEffect, useRef, useState } from "react";

/**
 * Cloudflare Turnstile widget — gratis CAPTCHA, privacy-vriendelijk.
 *
 * Activatie: zet NEXT_PUBLIC_TURNSTILE_SITE_KEY in env. Zonder key rendert
 * dit component niets (no-op, de form werkt onveranderd).
 *
 * Mode: "managed" (Cloudflare detecteert verdacht gedrag, voor 99% van legit
 * users invisible). Bij verdacht gedrag toont 'ie een 1-klik checkbox.
 *
 * Token-flow:
 *   1. Widget rendert + Cloudflare evalueert de browser
 *   2. Bij success: callback fires met token
 *   3. Form leest token via captchaTokenRef voor submit
 *   4. Backend (verifyTurnstile) checkt token bij Cloudflare API
 *
 * Token is single-use en max 5 min geldig — widget reset na elke submit
 * via de exposed `reset()` op de ref.
 */

interface TurnstileGlobal {
  render: (
    el: HTMLElement,
    options: {
      sitekey: string;
      callback?: (token: string) => void;
      "error-callback"?: () => void;
      "expired-callback"?: () => void;
      size?: "normal" | "compact" | "invisible" | "flexible";
      theme?: "light" | "dark" | "auto";
      appearance?: "always" | "execute" | "interaction-only";
    }
  ) => string;
  reset: (widgetId?: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileGlobal;
  }
}

interface Props {
  /** Hier komt de fresh token in. Form leest 'm uit voor submit. */
  tokenRef: { current: string | null };
  /** Optioneel callback — bv. om de form te re-enabling als 't klaar is. */
  onReady?: () => void;
}

const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

export default function TurnstileWidget({ tokenRef, onReady }: Props) {
  const siteKey = process.env["NEXT_PUBLIC_TURNSTILE_SITE_KEY"];
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [scriptReady, setScriptReady] = useState(false);

  useEffect(() => {
    if (!siteKey) return undefined;
    // Load script once (alle widgets delen 'm).
    if (window.turnstile) {
      setScriptReady(true);
      return undefined;
    }
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_URL}"]`);
    if (existing) {
      const onLoad = () => setScriptReady(true);
      existing.addEventListener("load", onLoad);
      if (window.turnstile) setScriptReady(true);
      return () => existing.removeEventListener("load", onLoad);
    }
    const s = document.createElement("script");
    s.src = SCRIPT_URL;
    s.async = true;
    s.defer = true;
    s.onload = () => setScriptReady(true);
    document.head.appendChild(s);
    return undefined;
  }, [siteKey]);

  useEffect(() => {
    if (!siteKey || !scriptReady || !containerRef.current || !window.turnstile) return;
    if (widgetIdRef.current) return; // already rendered
    widgetIdRef.current = window.turnstile.render(containerRef.current, {
      sitekey: siteKey,
      callback: (token: string) => {
        tokenRef.current = token;
        onReady?.();
      },
      "expired-callback": () => {
        tokenRef.current = null;
        // Auto-reset → krijgt verse token bij volgende submit-poging
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current);
        }
      },
      "error-callback": () => {
        tokenRef.current = null;
      },
      size: "flexible",
      appearance: "interaction-only", // alleen tonen als Cloudflare twijfel heeft
    });
    return () => {
      // Geen cleanup nodig — render() is idempotent en widget verdwijnt met DOM
    };
  }, [siteKey, scriptReady, tokenRef, onReady]);

  if (!siteKey) return null;
  return (
    <div ref={containerRef} className="cf-turnstile-host" style={{ minHeight: 0 }} aria-hidden="true" />
  );
}
